import type {ChatAttachment} from './types'

export type AttachmentKind = 'image' | 'video' | 'pdf' | 'word' | 'spreadsheet' | 'presentation' | 'text' | 'file'
export type AttachmentPreviewKind = 'image' | 'pdf' | 'markdown' | 'text' | 'json' | 'csv' | 'download'

function extension(name: string): string {
  return name.toLowerCase().split('.').pop() ?? ''
}

export function attachmentKind(attachment: Pick<ChatAttachment, 'name' | 'mimeType'>): AttachmentKind {
  const mime = attachment.mimeType?.toLowerCase() ?? ''
  const ext = extension(attachment.name)
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image'
  if (mime.startsWith('video/') || ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv'].includes(ext)) return 'video'
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (mime.includes('word') || ['doc', 'docx', 'odt'].includes(ext)) return 'word'
  if (mime.includes('spreadsheet') || mime.includes('excel') || ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'tsv', 'ods', 'numbers'].includes(ext)) return 'spreadsheet'
  if (mime.includes('presentation') || ['ppt', 'pptx', 'odp', 'key'].includes(ext)) return 'presentation'
  if (mime.startsWith('text/') || ['txt', 'md', 'rtf', 'yaml', 'yml', 'json', 'xml'].includes(ext)) return 'text'
  return 'file'
}

/** Formats that Ros can faithfully show without executing active document content. Office files
 * stay downloadable: browser-side DOCX/XLSX conversion is heavyweight and can turn untrusted
 * document content into active HTML. Ros can still inspect those files through its bounded reader. */
export function attachmentPreviewKind(attachment: Pick<ChatAttachment, 'name' | 'mimeType'>): AttachmentPreviewKind {
  const mime = attachment.mimeType?.toLowerCase() ?? ''
  const ext = extension(attachment.name)
  if (attachmentKind(attachment) === 'image') return 'image'
  if (attachmentKind(attachment) === 'pdf') return 'pdf'
  if (ext === 'md' || mime === 'text/markdown') return 'markdown'
  if (ext === 'json' || mime === 'application/json') return 'json'
  if (['csv', 'tsv'].includes(ext) || ['text/csv', 'text/tab-separated-values'].includes(mime)) return 'csv'
  if (mime.startsWith('text/') || ['txt', 'yaml', 'yml', 'xml', 'rtf'].includes(ext)) return 'text'
  return 'download'
}

export const ATTACHMENT_KIND_LABEL: Record<AttachmentKind, string> = {
  image: 'Image', video: 'Video', pdf: 'PDF', word: 'Word document', spreadsheet: 'Spreadsheet',
  presentation: 'Presentation', text: 'Text document', file: 'Attachment',
}
